import { Button } from '@ksyos/design-system';
import { useEffect, useRef, useState } from 'react';
import type { RecorderType } from '@common/enums';
import type { HolterConfigureData } from '@common/validation/customControls/cardiology/holter/holterConfigureSchemas';
import type { HolterDirectExaminationRequestData } from '@common/validation/customControls/cardiology/holter/holterDirectExaminationRequestSchemas';
import { HardwareProcessStatus } from '@common/enums/hardwareProcessStatus';
import { openKsyosUrl, OpenKsyosUrlResult } from '@frontend/components/elements/openKsyosUrl';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import { HolterInstructionModal } from '@frontend/components/pages/episodeCategory/customControls/cardiology/holter/customComponents/HolterInstructionModal';
import { PatientEpisodeCategoryContext } from '@frontend/components/pages/episodeCategory/patientEpisodeCategoryContext';
import { getDesktopProcess, getDesktopProcessIdAndUrl } from '@frontend/services/api/desktopProcessApiService';
import {
    getProcessActivityCustomControlData,
    getProcessActivityHolterDiaryUrl,
} from '@frontend/services/api/processActivitiesApiService';
import { useProcessActivityEditContext } from '@frontend/utils/hooks';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { HolterConfigureProcessingModal } from './HolterConfigureProcessingModal';

const timeoutIntervalSeconds = 2;
const timeoutIntervalMilliseconds = timeoutIntervalSeconds * 1000;
const maxWaitTimeSeconds = 120;

export type HolterConfigureButtonProps = Readonly<{
    name: string;
    onChange: (
        data: Partial<HolterConfigureData | HolterDirectExaminationRequestData>,
        validateKeys?: string | string[]
    ) => void;
    onValidate?: () => Promise<boolean>;
    processActivityId?: string;
    showHolterInstructionModal?: boolean;
}>;

export function HolterConfigureButton(props: HolterConfigureButtonProps) {
    const abortSignal = useAbortSignal();
    const { setActivityData } = useProcessActivityEditContext<
        HolterDirectExaminationRequestData | HolterConfigureData
    >();
    const { patient } = usePatientEpisodeCategoryContext();

    const { name, onChange, onValidate, showHolterInstructionModal } = props;
    const { reload } = useDefinedContext(PatientEpisodeCategoryContext);
    const { isSavingAndReloading, setActiveStartingPoint } = useDefinedContext(PatientEpisodeCategoryCarePathContext);

    const [isHolterInstructionModalOpen, setIsHolterInstructionModalOpen] = useState<boolean>(false);
    const [recorderType, setRecorderType] = useState<RecorderType | undefined>(undefined);
    const [timeWaited, setTimeWaited] = useState(0);
    const [processStatus, setProcessStatus] = useState<HardwareProcessStatus | undefined>(undefined);
    const [showProcessingModal, setShowProcessingModal] = useState(false);
    const [timeout, setTimeout] = useState<number | undefined>(undefined);

    // Use a ref to store the latest onValidate function
    const onValidateRef = useRef(onValidate);

    // Update the ref whenever onValidate changes
    useEffect(() => {
        onValidateRef.current = onValidate;
    }, [onValidate]);

    return (
        <>
            <Button
                isDisabled={isSavingAndReloading}
                onClick={async () => await onConfigureHolter()}
                variant="solid"
            >
                {name}
            </Button>
            {isHolterInstructionModalOpen && recorderType && props.processActivityId && (
                <HolterInstructionModal
                    holterDiaryUrl={getProcessActivityHolterDiaryUrl(props.processActivityId, patient)}
                    onClose={async () => {
                        setIsHolterInstructionModalOpen(false);
                        onSuccessHandler();
                    }}
                    recorderType={recorderType}
                />
            )}
            {showProcessingModal && <HolterConfigureProcessingModal processStatus={processStatus} />}
        </>
    );

    async function onConfigureHolter() {
        if (!props.processActivityId) {
            return;
        }

        if (onValidateRef.current) {
            const hasErrors = await onValidateRef.current();

            if (hasErrors) {
                return;
            }
        }

        const { id, url } = await getDesktopProcessIdAndUrl(props.processActivityId);

        setShowProcessingModal(true);

        if (!url) {
            onChange({ isMissingKsyosUrlHandler: true });
            setShowProcessingModal(false);
        }

        const openResult = await openKsyosUrl(url);

        if (openResult !== OpenKsyosUrlResult.success) {
            await onMissingKsyosUrlHandler();
        } else {
            await desktopStatusInterval(props.processActivityId, id);
        }
    }

    async function desktopStatusInterval(processActivityId: string, desktopProcessId: string) {
        try {
            const desktopProcess = await getDesktopProcess(desktopProcessId, abortSignal);
            if (
                desktopProcess.status === HardwareProcessStatus.starting ||
                desktopProcess.status === HardwareProcessStatus.running
            ) {
                setProcessStatus(desktopProcess.status);

                if (timeWaited >= maxWaitTimeSeconds) {
                    await onMissingKsyosUrlHandler();
                } else {
                    setTimeWaited(timeWaited + timeoutIntervalSeconds);
                    setTimeout(
                        window.setTimeout(async () => {
                            await desktopStatusInterval(processActivityId, desktopProcessId);
                        }, timeoutIntervalMilliseconds)
                    );
                }
            } else if (desktopProcess.status === HardwareProcessStatus.completed) {
                if (desktopProcess.data?.error) {
                    console.log('desktopProcess heeft een error', desktopProcess.data.error);

                    await onFailedHandler(desktopProcess.data.error);
                } else {
                    console.log('desktopProcess heeft geen error');
                    if (showHolterInstructionModal) {
                        setIsHolterInstructionModalOpen(true);
                        setRecorderType(desktopProcess.data?.recorderType);
                    } else {
                        onSuccessHandler();
                    }
                }
            }
        } catch (error) {
            console.log(error);
            await onFailedHandler();
        }
    }

    async function onMissingKsyosUrlHandler() {
        onChange({ isMissingKsyosUrlHandler: true });
        stopDesktopStatusInterval();
    }

    async function onFailedHandler(error?: string) {
        stopDesktopStatusInterval();
        await reloadProcessActivity(props.processActivityId, error);
    }

    function onSuccessHandler() {
        stopDesktopStatusInterval();
        setActiveStartingPoint(undefined);
        reload();
    }

    function stopDesktopStatusInterval() {
        window.clearTimeout(timeout);
        setShowProcessingModal(false);
    }

    async function reloadProcessActivity(processActivityId?: string, error?: string) {
        if (!processActivityId) {
            return;
        }
        const customControlData = await getProcessActivityCustomControlData(processActivityId, abortSignal);

        setActivityData({
            ...customControlData,
            processError: customControlData.error ?? error,
            processStatus: HardwareProcessStatus.failed,
        });
    }
}
